import { Trans, useLingui } from "@lingui/react/macro"
import type { SearchResult } from "@/types/v2/search"
import { SearchResultRow } from "./search-result-row"

// Sort modes for the results list header control. `relevance`
// keeps the server's order (best matches first); `recent`
// reorders client-side by the result's timestamp. The wire
// contract (Contract A) doesn't carry sort, so this is a
// display-only affordance over the already-fetched rows —
// kept functional (it really reorders) rather than a dead
// toggle.
export type SearchSort = "relevance" | "recent"

interface SearchResultsListProps {
  results: SearchResult[]
  query: string
  sort: SearchSort
  onSortChange: (sort: SearchSort) => void
  // Index of the keyboard-active row.
  activeIndex: number
  onHoverIndex: (index: number) => void
  // Record an opened result into recents.
  onActivate: (result: SearchResult) => void
  // Mobile rows show a trailing chevron + tap-to-navigate.
  showChevron?: boolean
  // Hide the header sort control on mobile (full-screen
  // variant keeps the chrome lean).
  showSort?: boolean
}

export function SearchResultsList({
  results,
  query,
  sort,
  onSortChange,
  activeIndex,
  onHoverIndex,
  onActivate,
  showChevron = false,
  showSort = true,
}: SearchResultsListProps) {
  const { t } = useLingui()
  const ordered = sortResults(results, sort)

  return (
    <div className="flex min-w-0 flex-col">
      <div className="flex items-center justify-between px-3 pb-1.5 pt-1">
        <span
          data-testid="search-results-count"
          className="text-[11px] uppercase leading-3 font-semibold tracking-[0.06em] text-[#9A938A]"
        >
          {t`Search results (${results.length})`}
        </span>
        {showSort && results.length > 0 ? (
          <SortControl sort={sort} onSortChange={onSortChange} />
        ) : null}
      </div>

      {ordered.length === 0 ? (
        <EmptyState query={query} />
      ) : (
        <div className="flex flex-col">
          {ordered.map((result, index) => (
            // `index` is the row's position in the displayed
            // (sorted) order, so ↑/↓ + the active highlight match
            // what the user sees after a re-sort.
            <SearchResultRow
              key={result.key}
              result={result}
              active={index === activeIndex}
              onHover={() => onHoverIndex(index)}
              onActivate={() => onActivate(result)}
              showChevron={showChevron}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Stable, display-only reordering. `relevance` is the
// server's order untouched. `recent` sorts newest-first by
// the row timestamp; rows without a timestamp sink to the
// bottom in their original relative order.
export function sortResults(
  results: SearchResult[],
  sort: SearchSort,
): SearchResult[] {
  if (sort === "relevance") return results
  return results
    .map((result, index) => ({ result, index }))
    .sort((a, b) => {
      const at = timeValue(a.result.timestampLabel, a.index)
      const bt = timeValue(b.result.timestampLabel, b.index)
      if (at.bucket !== bt.bucket) return at.bucket - bt.bucket
      // Within the same bucket keep server order.
      return a.index - b.index
    })
    .map(({ result }) => result)
}

// The timestamp label is a display string ("2h", "Mar 14"),
// not a sortable instant — the backend doesn't ship a raw
// timestamp on each row. So "Most recent" can only partition
// labelled rows ahead of unlabelled ones and otherwise keep
// the server's (already recency-aware) order. Real
// instant-level sorting would need the contract to carry a
// sortable field; that's a backend follow-up, not a client
// guess.
function timeValue(
  label: string | null,
  _index: number,
): { bucket: number } {
  return { bucket: label ? 0 : 1 }
}

function SortControl({
  sort,
  onSortChange,
}: {
  sort: SearchSort
  onSortChange: (sort: SearchSort) => void
}) {
  const { t } = useLingui()
  return (
    <div
      role="group"
      aria-label={t`Sort results`}
      className="flex items-center gap-0.5 rounded-full bg-[#F0EDE6] p-0.5"
    >
      <SortPill
        active={sort === "relevance"}
        onClick={() => onSortChange("relevance")}
        testId="sort-relevance"
      >
        <Trans>Best matches</Trans>
      </SortPill>
      <SortPill
        active={sort === "recent"}
        onClick={() => onSortChange("recent")}
        testId="sort-recent"
      >
        <Trans>Most recent</Trans>
      </SortPill>
    </div>
  )
}

function SortPill({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean
  onClick: () => void
  testId: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`rounded-full px-2.5 py-1 text-[11px] leading-3 ${
        active
          ? "bg-white font-medium text-[#1E1B17] shadow-[0_1px_2px_rgba(30,27,23,0.08)]"
          : "text-[#7A746B]"
      }`}
    >
      {children}
    </button>
  )
}

function EmptyState({ query }: { query: string }) {
  return (
    <div
      data-testid="search-empty-state"
      className="flex flex-col items-center gap-1 px-6 py-10 text-center"
    >
      <span className="text-[13px] leading-[18px] font-medium text-[#1E1B17]">
        <Trans>Nothing matches &ldquo;{query}&rdquo; yet</Trans>
      </span>
      <span className="text-[12px] leading-4 text-[#9A938A]">
        <Trans>Try a name, phone number, or a few words from a note.</Trans>
      </span>
    </div>
  )
}
